import {
  forwardRef,
  useImperativeHandle,
  useRef,
  type ReactNode,
} from "react";
import { cn } from "@/shared/lib/cn";
import { InputGroup, TextArea, type TextAreaProps } from "./Field";
import { DictationButton } from "./DictationButton";

/**
 * Shared prompt surface used by every creation category.
 *
 * Media-specific actions (references, AI assistance, counters, or secondary
 * fields) can be supplied as children so the input itself keeps one consistent
 * interaction and visual treatment everywhere.
 */
export interface PromptInputProps extends TextAreaProps {
  children?: ReactNode;
  groupClassName?: string;
  dictation?: boolean;
}

/** The shared textarea itself for contexts that already provide a Field. */
export const PromptTextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  function PromptTextArea({ className, ...props }, ref) {
    return (
      <TextArea
        {...props}
        ref={ref}
        variant={props.variant ?? "plain"}
        className={cn(
          "min-h-28 w-full resize-none overflow-hidden bg-transparent px-3 py-2 font-sans text-base leading-[1.6] text-ink outline-none placeholder:text-ink-muted",
          className,
        )}
      />
    );
  },
);

export const PromptInput = forwardRef<HTMLTextAreaElement, PromptInputProps>(
  function PromptInput(
    { children, className, groupClassName, dictation = true, ...props },
    ref,
  ) {
    const target = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(ref, () => target.current!);
    return (
      <InputGroup className={cn("studio-prompt-input", groupClassName)}>
        <PromptTextArea {...props} ref={target} className={className} />
        {dictation && !props.readOnly && <DictationButton target={target} disabled={props.disabled} />}
        {children}
      </InputGroup>
    );
  },
);
